import React from 'react';
import { format } from 'date-fns';
import { Task } from '../kanban/types.ts'
import { Calendar, Flag, ArrowRight, CheckCircle2 } from 'lucide-react';

const getPriorityStyle = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'low':
      return {
        color: 'text-green-600',
        bg: 'bg-green-50',
        border: 'border-green-200'
      };
    case 'medium':
      return {
        color: 'text-orange-600',
        bg: 'bg-orange-50',
        border: 'border-orange-200'
      };
    case 'high':
      return {
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200'
      };
    default:
      return {
        color: 'text-green-600',
        bg: 'bg-green-50',
        border: 'border-green-200'
      };
  }
};

interface TaskListProps {
  tasks: Task[];
  emptyMessage: string;
  onNavigateToTaskboard: () => void;
  compact?: boolean;
}

export const TaskList: React.FC<TaskListProps> = ({ 
  tasks, 
  emptyMessage, 
  onNavigateToTaskboard, 
  compact = false 
}) => {
  if (tasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
          <CheckCircle2 className="w-5 h-5 text-gray-400" />
        </div>
        <p className="text-sm text-gray-500 mb-4">{emptyMessage}</p>
        <button
          onClick={onNavigateToTaskboard}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
        >
          Add new task
        </button>
      </div>
    );
  }

  const displayTasks = compact ? tasks.slice(0, 3) : tasks;

  return (
    <div className="space-y-3">
      {displayTasks.map(task => {
        const priorityStyle = getPriorityStyle(task.priority);
        
        return (
          <div
            key={task.id}
            className="group p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 shadow-sm"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h5 className="text-sm font-medium text-gray-900 truncate">{task.name}</h5>
                  {task.dueDate && (
                  <div className="flex items-center mt-1 text-xs text-gray-500">
                    <Calendar className="w-3 h-3 mr-1" />
                    <span>{format(new Date(task.dueDate), 'MMM d, yyyy')}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2 ml-3">
                <div className={`flex items-center px-2 py-1 rounded-md border ${priorityStyle.bg} ${priorityStyle.border}`}>
                  <Flag className={`w-3 h-3 mr-1 ${priorityStyle.color}`} />
                  <span className={`text-xs font-medium ${priorityStyle.color}`}>
                    {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      
      {compact && tasks.length > 3 && (
        <div className="text-center pt-2">
          <span className="text-xs text-gray-500">+{tasks.length - 3} more</span>
        </div>
      )}
      
      <button
        onClick={onNavigateToTaskboard}
        className="w-full mt-4 flex items-center justify-center space-x-2 px-4 py-2 
          border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300
          transition-all duration-200 group"
      >
        <span className="text-sm font-medium text-gray-700">
          {compact ? 'View all tasks' : 'Go to task board'}
        </span>
        <ArrowRight className="w-4 h-4 text-gray-500 transform group-hover:translate-x-1 transition-transform" />
      </button>
    </div>
  );
};